import { parse, ObjectSchema, InferOutput } from "valibot";
import { PointVisibilitySchema } from "../point/point-history/point-history-selection-state";

/**
 * Serializes an object into a base64 string that can be used as a URL parameter.
 *
 * Note that using base64 allows us to store more data in the URL without hitting
 * the browser's URL length limit compared to using `encodeURIComponent` and
 * `JSON.stringify` directly.
 */
export function serializeObjectParam<T extends Record<string, unknown>>(
  obj: T,
): string {
  const jsonString = JSON.stringify(obj);
  return window.btoa(jsonString);
}

/**
 * Deserializes a base64 string into an object.
 *
 * Note that this doesn't validate the object against a schema, please use
 * `parseObjectParam` for both deserialization and validation.
 */
export function deserializeObjectParam<T = unknown>(
  serializedString: string,
): T {
  const decodedString = window.atob(serializedString);
  return JSON.parse(decodedString);
}

/**
 * Parses a URL parameter that represents an object and validates it against a schema.
 *
 * If the value is null or doesn't conform to the provided schema, the default value is returned.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function parseObjectParam<T extends ObjectSchema<any, any>>({
  value,
  defaultValue,
  schema,
}: {
  value: string | null;
  defaultValue: InferOutput<T>;
  schema: T;
}): InferOutput<T> {
  if (value === null) {
    return defaultValue;
  }

  try {
    return parse(schema, deserializeObjectParam(value), {
      // We want to abort early to avoid parsing the whole object if there are any errors
      abortEarly: true,
    });
  } catch (_error) {
    return defaultValue;
  }
}
export function serializeHistoryParams(schema: PointVisibilitySchema) {
  return serializeObjectParam(schema);
}
